/**
 * Which build this tab is running, and which build the site is serving now.
 *
 * A deploy replaces `/static/js/main.<hash>.js` and rewrites `index.html` to point at it, but a tab
 * that is already open keeps the bundle it loaded: there is no service worker and nothing tells the
 * page that a newer release exists. On 2026-10-29 a fix that had been live for nearly two hours came
 * back as a Critical ticket from a second user, because their tab had been open since the morning.
 *
 * `index.html` is served `cache-control: no-cache`, so it is the reliable thing to re-read; the
 * hashed bundle it names is the version.
 */

const MAIN_BUNDLE = /\/static\/js\/main\.[A-Za-z0-9_-]+\.js/

/**
 * Path of the main bundle THIS tab is running, e.g. `/static/js/main.1f9a9c81.js`.
 * `null` under the dev server (which serves `bundle.js`) and anywhere the tag is absent, which is
 * what keeps the whole check out of the way outside a production build.
 */
export const loadedBundlePath = (): string | null => {
  if (typeof document === 'undefined') return null
  const script = document.querySelector<HTMLScriptElement>('script[src*="/static/js/main."]')
  if (!script?.src) return null
  const match = script.src.match(MAIN_BUNDLE)
  return match ? match[0] : null
}

/** The main bundle named by a given `index.html`, or `null` if it names none. */
export const parseBundlePath = (html: string): string | null => {
  const match = html.match(MAIN_BUNDLE)
  return match ? match[0] : null
}

/**
 * Path of the main bundle the site is serving right now. Resolves to `null` on any network or
 * parse trouble: a failed check must never surface to the user, it just means we look again later.
 */
export const fetchDeployedBundlePath = async (signal?: AbortSignal): Promise<string | null> => {
  try {
    const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store', signal })
    if (!res.ok) return null
    return parseBundlePath(await res.text())
  } catch {
    return null
  }
}
